/**
 * Events: the device's Windows event logs, read live.
 *
 * Nothing here is stored by the CRM - this reads the machine's own logs as the agent, which is
 * SYSTEM, so the Security log answers where a signed-in user would be refused. The consequence is
 * that it needs the device online, and that a query is expensive: **the first one runs on this tab's
 * first reveal, never at page load.** A live round trip to a machine, paid for on every device page
 * opened to read the Overview, is a cost with no reader.
 *
 * Almost all the filtering happens on the DEVICE. Level, event ID and time range become an XPath
 * prefilter the log evaluates itself, which is what keeps a query against a 100,000-entry Security
 * log fast; the free-text search has no XPath equivalent worth having, because an event's message is
 * formatted at read time from the provider's template and simply is not in the log, so the agent
 * matches it in code while enumerating. Only matches travel.
 *
 * **The two truncations are reported apart**, which is the one piece of wording on this tab that has
 * to be right: "the log holds more" and "I stopped looking" are different facts, and conflating them
 * tells a technician there is more to find when the truth is that the search gave up.
 */
import type { Control } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import { maxEvents, maxEventScan } from '../../api/settings.js';
import type { EventLogEntryDto } from '../../contracts.gen.js';
import { localDateTime, plural, trim } from '../../format.js';
import { CardList } from '../../ui/CardList.js';
import { BoundedScroller } from '../../ui/Scroller.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, comboBox, field, fieldRow, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, splitRow, stackOf, text, textBox, value, vstack } from '../../ui/layout.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

/** A defensive render cap; the server already clamps to the event-log setting. */
const MAX_EVENT_ROWS = 250;

/** How much of a message the collapsed row shows. */
const SUMMARY_CHARS = 180;

/** Windows level values. Null is every level; Information covers LogAlways agent-side. */
const LEVELS: readonly { readonly label: string; readonly level: number | null }[] = [
  { label: 'Any level', level: null },
  { label: 'Critical', level: 1 },
  { label: 'Error', level: 2 },
  { label: 'Warning', level: 3 },
  { label: 'Information', level: 4 },
  { label: 'Verbose', level: 5 }
];

const RANGES: readonly { readonly label: string; readonly ms: number | null }[] = [
  { label: 'Last hour', ms: 60 * 60 * 1000 },
  { label: 'Last 12 hours', ms: 12 * 60 * 60 * 1000 },
  { label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
  { label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  { label: 'Last 30 days', ms: 30 * 24 * 60 * 60 * 1000 },
  { label: 'Any time', ms: null }
];

export interface EventsTab extends DeviceTab {
  applyPresence(): void;
}

export function eventsTab(dc: DeviceTabContext): EventsTab {
  let busy = false;
  let queried = false;
  const expanded = new Set<number>();

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const status = new StatusLine();

  // ---- log picker ----
  const logBox = textBox({ text: 'System', hAlign: 'stretch' });
  const quickLogs = stackOf(
    buttonRow({ hAlign: 'end' }),
    ...['System', 'Application', 'Security'].map(name =>
      button(name, () => {
        logBox.text = name;
        void search();
      })
    )
  );
  const logRow = splitRow({ align: 'center' });
  fillWith(logRow, logBox);
  logRow.controls.add(quickLogs);

  // ---- filters ----
  const levelPicker = comboBox(LEVELS, { itemText: l => l.label, selectedIndex: 0 });
  // 24 hours, matching the XAML: an hour is too short to find the thing somebody is looking for and
  // "any time" on a Security log is a scan nobody wants by default.
  const rangePicker = comboBox(RANGES, { itemText: r => r.label, selectedIndex: 2 });
  const eventIdBox = textBox();
  const searchBox = textBox();
  const filters = fieldRow(
    field('Level', levelPicker),
    field('Time range', rangePicker),
    field('Event ID (blank = any)', eventIdBox),
    field('Search the message, source, or level', searchBox)
  );

  const searchButton = button('Search', () => void search(), true);
  const resetButton = button('Reset filters', () => void reset());
  const actions = stackOf(buttonRow(), searchButton, resetButton);

  const waiting = muted(
    'Pick a log and press Search — the query runs on the device, so it needs to be online.'
  );

  const rows = new CardList<EventLogEntryDto>({
    key: e => `${e.timeUtc ?? ''}|${e.eventId}|${e.provider}`,
    // The expansion state is part of what a row draws, so a row whose toggle moved has to rebuild.
    version: e => expanded.has(rowKey(e)),
    build: e => buildRow(e),
    max: MAX_EVENT_ROWS,
    spacing: 0
  });
  const scroller = new BoundedScroller(420);
  scroller.content.controls.add(rows.panel);

  const body = card();
  body.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      logRow,
      text('Log name', { role: 'muted', font: FONT.tiny }),
      filters,
      actions,
      status.label,
      waiting,
      scroller,
      text(
        'Read live from the device as the agent (SYSTEM) — nothing is stored here. Level, event ID and ' +
          'time range are applied inside the log itself; the text search scans the matching events ' +
          'newest-first.',
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );

  panel.controls.addRange([h2('Event log'), body]);

  // ---------------------------------------------------------------
  //  Rows
  // ---------------------------------------------------------------

  /**
   * A stable-enough identity for the expansion set.
   *
   * The entries carry no id of their own - a log record number never reaches the DTO - so this is a
   * hash of what does identify one: when it happened, which event it was, and who wrote it.
   */
  function rowKey(entry: EventLogEntryDto): number {
    const source = `${entry.timeUtc ?? ''}|${entry.eventId}|${entry.provider}`;
    let hash = 0;
    for (let i = 0; i < source.length; i++) hash = (hash * 31 + source.charCodeAt(i)) | 0;
    return hash;
  }

  function buildRow(entry: EventLogEntryDto): Control {
    const key = rowKey(entry);
    const message = entry.message.trim();
    const firstBreak = message.indexOf('\n');
    const summary = (firstBreak >= 0 ? message.slice(0, firstBreak) : message).trim();
    const firstLine = trim(summary, SUMMARY_CHARS);
    const hasDetail = message.length > firstLine.length;
    const open = expanded.has(key);

    // The level drives the colour so a screen of rows reads at a glance - the same green/amber/red
    // language the temperature legend uses.
    const heading = inlineRow();
    heading.controls.add(
      value(entry.levelName || 'Information', {
        font: FONT.bold,
        role: entry.level === 1 || entry.level === 2 ? 'danger' : entry.level === 3 ? 'warn' : 'muted'
      })
    );
    heading.controls.add(muted(entry.timeUtc ? localDateTime(entry.timeUtc) : '—'));
    heading.controls.add(muted(`Event ${entry.eventId}  ·  ${entry.provider || '—'}`));

    const left = stackOf(
      vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
      heading,
      value(open ? message : firstLine, { wrap: open, font: FONT.small })
    );

    const row = splitRow({ spacing: GAP.md, align: 'start' });
    fillWith(row, left);
    if (hasDetail) {
      row.controls.add(
        button(open ? 'Hide' : 'Details', () => {
          if (open) expanded.delete(key);
          else expanded.add(key);
          // Only this row rebuilds: the keyed diff sees one changed version and leaves the rest.
          rows.refresh();
          scroller.fit();
          dc.relayout();
        })
      );
    }
    return row;
  }

  // ---------------------------------------------------------------
  //  Querying
  // ---------------------------------------------------------------

  async function search(): Promise<void> {
    if (busy) return;
    busy = true;
    queried = true;
    searchButton.enabled = false;
    status.busy('Reading the log…');
    waiting.visible = false;
    dc.relayout();

    try {
      const parsedId = Number.parseInt(eventIdBox.text.trim(), 10);
      const range = rangePicker.selectedItem ?? RANGES[2]!;
      const result = await crm.queryDeviceEventLog(
        dc.deviceId,
        {
          logName: logBox.text.trim(),
          level: levelPicker.selectedItem?.level ?? null,
          eventId: Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null,
          search: searchBox.text.trim() || null,
          fromUtc: range.ms === null ? null : new Date(Date.now() - range.ms).toISOString(),
          toUtc: null,
          maxEvents: maxEvents()
        },
        dc.ctx.dispose.signal()
      );
      if (dc.ctx.cancelled()) return;

      if (!result.success) {
        rows.set([]);
        status.error(result.error ?? 'Could not read the event log.');
        return;
      }

      expanded.clear();
      rows.set(result.entries);
      scroller.fit();

      const notes: string[] = [
        result.entries.length === 0
          ? 'No events matched these filters.'
          : `${plural(Math.min(result.entries.length, MAX_EVENT_ROWS), 'event')}, newest first.`
      ];
      // Two different truncations, reported separately - see the note at the top of this file.
      if (result.truncated) {
        notes.push(
          `The log holds more — only the newest ${maxEvents()} matches are shown. Narrow the time ` +
            'range or filters to see further back.'
        );
      }
      if (result.scanTruncated) {
        notes.push(
          `The search stopped after scanning ${maxEventScan()} events. Narrow the time range or add ` +
            'a level filter to search deeper.'
        );
      }
      status.set(notes.join('  '));
    } catch (error) {
      if (!dc.ctx.cancelled()) {
        rows.set([]);
        status.error(describeError(error));
      }
    } finally {
      busy = false;
      searchButton.enabled = dc.isOnline();
      waiting.visible = !queried;
      dc.relayout();
    }
  }

  /** Back to the defaults - System, every level, the last 24 hours - and re-query. */
  async function reset(): Promise<void> {
    logBox.text = 'System';
    levelPicker.selectedIndex = 0;
    rangePicker.selectedIndex = 2;
    eventIdBox.text = '';
    searchBox.text = '';
    await search();
  }

  function applyPresence(): void {
    const online = dc.isOnline();
    searchButton.enabled = online && !busy;
    resetButton.enabled = online && !busy;
    for (const b of quickLogs.controls) b.enabled = online && !busy;
  }

  applyPresence();

  return {
    panel,
    onShow() {
      if (queried || busy || !dc.isOnline()) return;
      void search();
    },
    applyPresence
  };
}
